import React, { useEffect, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ButtonGroup } from '@paygreen/paygreen-ui';
import { useAPIError } from '../../../../context';
import { apiAuth } from '../../../../utils/hooks';
import {
    CancelButton,
    ValidateButton,
    ModalForm,
} from '../../../../components';
import AdministratorManagementForm from './AdministratorManagementForm';

const AdministratorManagementModal = ({
    t,
    setOpen,
    isOpen,
    accountData,
    setAccountData,
    setIsLoading,
    isShop,
    refresh,
}) => {
    const { addError } = useAPIError();
    const object = isShop ? 'administrator' : 'representative';

    // initializing all react-hook-form values and methods
    const defaultValues = {
        administrator:
            accountData?.[object].firstname +
            ' ' +
            accountData?.[object].lastname,
        email: accountData?.[object].contact.email || '',
        phone: accountData?.[object].contact.phone?.slice(1) || '', // the '' is necessary to avoid warning from useController in phone input that doesn't like undefined value
    };

    const {
        register,
        handleSubmit,
        setValue,
        errors,
        clearErrors,
        control,
        reset,
    } = useForm();

    const accountId = accountData?.[object].accountId;
    const username = accountData?.[object].username;
    const firstname = accountData?.[object].firstname;
    const lastname = accountData?.[object].lastname;

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'email' });
        register({ name: 'phone' });
        register({ name: 'administrator' });
    }, [register]);

    useEffect(() => {
        reset({
            administrator: firstname + ' ' + lastname,
            email: accountData?.[object].contact.email,
            phone: accountData?.[object].contact.phone?.slice(1) || '',
        });
    }, [accountData, reset, object, firstname, lastname]);

    /**
     * PATCH request to API to update user's account
     * @param {object} updatedEmail - object with email updated
     */
    const updateEmail = useCallback(
        (updatedData) => {
            setIsLoading(true);

            apiAuth()
                .patch(`/account/${accountId}/user/${username}`, updatedData)
                .then(() => {
                    setAccountData({
                        ...accountData,
                        [object]: {
                            ...accountData[object],
                            contact: {
                                ...accountData[object].contact,
                                email: updatedData.email,
                                phone: updatedData.phone,
                            },
                        },
                    });
                    refresh();
                    toast(t('form.toastSuccess.adminUserEmailUpdate'));
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    addError(detail, status);
                })
                .finally(() => setIsLoading(false));
        },
        [
            addError,
            setIsLoading,
            accountId,
            username,
            setAccountData,
            accountData,
            object,
            refresh,
            t,
        ],
    );

    const submitForm = (data) => {
        setOpen(false);
        const updatedAdmin = {
            phone: data.phone.replace(/\(0\)|[\s]|\+\) /g, ''),
            email: data.email,
        };

        updateEmail(updatedAdmin);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        setValue(name, value);
    };

    const cancelCloseModal = () => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            clearErrors();
            reset(defaultValues);
        }, 500);
    };

    return (
        <ModalForm
            modalTitle={t('user.edit.title')}
            blockWidth="md"
            paddingLateral="md"
            isOpen={isOpen}
            actionOnClose={() => cancelCloseModal()}
        >
            <AdministratorManagementForm
                errors={errors}
                handleChange={handleChange}
                register={register}
                control={control}
                onSubmit={handleSubmit(submitForm)}
                defaultValues={defaultValues}
            >
                <ButtonGroup marginTop="sm" marginBottom="sm">
                    <CancelButton
                        onClick={() => cancelCloseModal()}
                        buttonSize="md"
                    >
                        {t('form.button.cancel')}
                    </CancelButton>

                    <ValidateButton buttonSize="md">
                        {t('form.button.save')}
                    </ValidateButton>
                </ButtonGroup>
            </AdministratorManagementForm>
        </ModalForm>
    );
};

AdministratorManagementModal.propTypes = {
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    accountData: PropTypes.object,
    setIsLoading: PropTypes.func.isRequired,
    setAccountData: PropTypes.func.isRequired,
    isShop: PropTypes.bool,
    refresh: PropTypes.func,
};

AdministratorManagementModal.defaultProps = {
    isShop: true,
};

export default withTranslation('common')(AdministratorManagementModal);
